/**
 * governance/systemStats.ts
 * Computes LIVE system statistics for the developer dashboard.
 *
 * PHASE 3 MIGRATION: Reduced dependency on legacy rulebookRegistry.
 * QUESTION_TYPE_REGISTRY import kept (shared reference, not legacy).
 *
 * ARCHITECTURE v4.0 — Phase 3, Step 3.4
 *
 * NOTE: stab-ronde 3 (mei 2026) — `computeModuleStats` (≈30 regels) en
 * `computeCalibrationStats` (≈180 regels) verwijderd: beide hadden nul
 * callers in `src/**`. Het admin dashboard surfact deze metrics niet meer
 * sinds RC-8. Re-introduceer via een Phase-4 admin-UI als ze terugkomen.
 */

import { TAXONOMY, getAllModuleScopedSkills, ALL_QUESTIONS_CORE } from '../content';
import { QUESTION_TYPE_REGISTRY } from './questionTypeDefinitions';
import type { PipelineBatchResult } from './pipeline/issueContract';
import { countModuleRulebooks } from './rulebooks';
import { getPolicy } from './pipeline/policyEngine';

// ── GovernanceSystemStats (was in governance/types.ts, now inline) ─────────────

export interface GovernanceSystemStats {
  totalFamilies: number;
  totalSkills: number;
  totalQuestions: number;
  totalSeeds: number;
  registeredRulebooks: number;
  registeredQuestionTypes: number;
  validatorVersion: string;
  lastAuditTimestamp: number | null;
  overallQualityScore: number | null;
}

// ── Rule count ────────────────────────────────────────────────────────────────
// Phase 3 (Strak-Trek): rulebooks are now executable per-module rulebooks
// registered in src/governance/rulebooks/. The count below reflects the
// live registry.

/**
 * Computes live system statistics.
 * @param pipelineReport Optional pre-computed pipeline batch result
 */
export function computeLiveSystemStats(pipelineReport?: PipelineBatchResult): GovernanceSystemStats {
  const totalFamilies = TAXONOMY.length;
  const totalSkills = getAllModuleScopedSkills().length;
  const totalQuestions = ALL_QUESTIONS_CORE.length;
  const totalSeeds = totalQuestions;
  const totalRules = countModuleRulebooks();
  const totalQuestionTypes = Object.keys(QUESTION_TYPE_REGISTRY).length;
  const overallQualityScore = pipelineReport?.avgScore ?? null;
  const lastAuditTimestamp = pipelineReport ? Date.now() : null;
  return {
    totalFamilies,
    totalSkills,
    totalQuestions,
    totalSeeds,
    registeredRulebooks: totalRules,
    registeredQuestionTypes: totalQuestionTypes,
    // Validator version tracks the active policy semver (`DEFAULT_POLICY.version`
    // via policyEngine) so admin diagnostics never drift from the policy in use.
    // The `-pipeline` suffix identifies the v4.x pipeline architecture.
    validatorVersion: `${getPolicy().version}-pipeline`,
    lastAuditTimestamp,
    overallQualityScore
  };
}